import { create } from 'zustand';
import i18n from '../i18n';
import { clientBookingsApi, classifyCancelError } from '../api/clientBookingsApi';
import type {
  ClientBookingItem,
  SmartHint,
  CancellationReason,
  ErrorKind,
} from '../types/client';

// ─── Local types ──────────────────────────────────────────────────────────────

type Tab = 'active' | 'history';

type CancelState =
  | { kind: 'idle' }
  | { kind: 'reasonSelect'; bookingId: number; bookingUuid: string }
  | { kind: 'confirming'; bookingId: number; bookingUuid: string; reason: CancellationReason; comment?: string }
  | { kind: 'submitting'; bookingId: number; bookingUuid: string }
  | { kind: 'success'; bookingId: number }
  | { kind: 'error'; bookingId: number; message: string };

interface ClientBookingsState {
  active: ClientBookingItem[];
  history: ClientBookingItem[];
  activeCount: number;
  historyCount: number;
  historyCursor: string | null;
  historyHasMore: boolean;
  hint: SmartHint | null;
  tab: Tab;
  status: 'idle' | 'loading' | 'loading-more' | 'ready' | 'empty' | 'error';
  error: { kind: ErrorKind; message: string } | null;
  cancel: CancelState;
  actionBookingId: number | null;
  isFetching: boolean;

  setTab: (tab: Tab) => void;
  refresh: () => Promise<void>;
  loadMoreHistory: () => Promise<void>;
  openCancellation: (id: number, uuid: string) => void;
  setReason: (reason: CancellationReason, comment?: string) => void;
  confirmCancellation: () => Promise<void>;
  closeCancellation: () => void;
  clearError: () => void;
  reset: () => void;
}

// ─── Initial state ────────────────────────────────────────────────────────────

const initialState = {
  active: [] as ClientBookingItem[],
  history: [] as ClientBookingItem[],
  activeCount: 0,
  historyCount: 0,
  historyCursor: null as string | null,
  historyHasMore: false,
  hint: null as SmartHint | null,
  tab: 'active' as Tab,
  status: 'idle' as const,
  error: null as { kind: ErrorKind; message: string } | null,
  cancel: { kind: 'idle' } as CancelState,
  actionBookingId: null as number | null,
  isFetching: false,
};

// ─── Store ────────────────────────────────────────────────────────────────────

export const useClientBookingsStore = create<ClientBookingsState>((set, get) => ({
  ...initialState,

  setTab: (tab) => set({ tab }),

  refresh: async () => {
    if (get().isFetching) return;
    set({ isFetching: true, status: 'loading', error: null });

    const [activeResult, historyResult] = await Promise.allSettled([
      clientBookingsApi.getActive(),
      clientBookingsApi.getHistory(),
    ]);

    if (activeResult.status === 'rejected') {
      // Active tab ham yuklanmasa — to'liq xato holati
      const { kind } = classifyCancelError(activeResult.reason);
      set({
        status: 'error',
        error: { kind, message: "Bronlarni yuklab bo'lmadi" },
        isFetching: false,
      });
      return;
    }

    const activeRes = activeResult.value;
    const historyRes = historyResult.status === 'fulfilled' ? historyResult.value : null;

    const isEmpty = activeRes.items.length === 0 && (historyRes === null || historyRes.items.length === 0);

    set({
      active: activeRes.items,
      activeCount: activeRes.items.length,
      history: historyRes?.items ?? [],
      historyCount: historyRes?.items.length ?? 0,
      historyCursor: historyRes?.nextCursor ?? null,
      historyHasMore: (historyRes?.nextCursor ?? null) !== null,
      hint: activeRes.hint,
      status: isEmpty ? 'empty' : 'ready',
      // History yuklanmasa xatoni saqlaymiz lekin sahifani buzmaymiz
      error: historyResult.status === 'rejected'
        ? { kind: 'unknown', message: "Bron tarixi yuklanmadi" }
        : null,
      isFetching: false,
    });
  },

  loadMoreHistory: async () => {
    const { status, historyHasMore, historyCursor } = get();
    if (status === 'loading-more' || !historyHasMore) return;
    set({ status: 'loading-more' });
    try {
      const res = await clientBookingsApi.getHistory(historyCursor ?? undefined);
      set((state) => ({
        history: [...state.history, ...res.items],
        historyCount: state.historyCount + res.items.length,
        historyCursor: res.nextCursor,
        historyHasMore: res.nextCursor !== null,
        status: 'ready',
      }));
    } catch {
      // loading-more fail → silently reset to ready, existing data is preserved
      set({ status: 'ready' });
    }
  },

  openCancellation: (id, uuid) =>
    set({ cancel: { kind: 'reasonSelect', bookingId: id, bookingUuid: uuid }, actionBookingId: id }),

  setReason: (reason, comment) => {
    const { cancel } = get();
    if (cancel.kind !== 'reasonSelect') return;
    set({
      cancel: {
        kind: 'confirming',
        bookingId: cancel.bookingId,
        bookingUuid: cancel.bookingUuid,
        reason,
        comment,
      },
    });
  },

  confirmCancellation: async () => {
    const { cancel, active } = get();
    if (cancel.kind !== 'confirming') return;
    const { bookingId, bookingUuid, reason } = cancel;

    // Snapshot for rollback on failure
    const snapshot = [...active];
    set({
      cancel: { kind: 'submitting', bookingId, bookingUuid },
      active: active.map((b) =>
        b.id === bookingId
          ? { ...b, status: 'CANCELLED' as const, canRequestCancel: false }
          : b,
      ),
    });

    try {
      await clientBookingsApi.cancelBooking(bookingUuid, { reason });
      set({ cancel: { kind: 'success', bookingId } });

      // Auto-close after 400ms then refresh
      setTimeout(() => {
        const current = get().cancel;
        if (current.kind === 'success' && current.bookingId === bookingId) {
          set({ cancel: { kind: 'idle' }, actionBookingId: null });
          void get().refresh();
        }
      }, 400);
    } catch (err) {
      const classified = classifyCancelError(err);
      let errorMessage = "So'rovni yuborishda xatolik yuz berdi";
      if (classified.kind === 'deadline') errorMessage = 'Bekor qilish muddati tugagan';
      if (classified.kind === 'network' || classified.kind === 'offline')
        errorMessage = "Internet aloqasi yo'q. Qayta urinib ko'ring";

      // Rollback optimistic update
      set({
        cancel: { kind: 'error', bookingId, message: errorMessage },
        active: snapshot,
      });
    }
  },

  closeCancellation: () => set({ cancel: { kind: 'idle' }, actionBookingId: null }),

  clearError: () => set({ error: null }),

  reset: () => set(initialState),
}));

// Til o'zgargach booking item'lardagi serviceName/providerName Accept-Language
// yangi qiymati bilan qaytadan keladi. Modul-darajasi listener — BookingsTab
// unmount bo'lganda ham yo'qolmaydi.
if (typeof window !== 'undefined') {
  i18n.on('languageChanged', () => {
    const state = useClientBookingsStore.getState();
    // Active yoki history bo'sh emas — qayta yuklash mantiqiy
    if (state.active.length > 0 || state.history.length > 0) {
      void state.refresh();
    }
  });
}
